import { InputLabel } from '@components/ui';
import {
  Grid,
  InputAdornment,
  makeStyles,
  Slider as MuiSlider,
  TextField,
  TextFieldProps,
  Theme,
} from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { scaleLinear } from 'd3';
import { debounce } from 'lodash';
import { memo, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCurrency } from '../hooks/useCurrency';
import { useDidUpdateEffect } from '../hooks/useDidUpdateEffect';
import { DEFAULT_CURRENCY, MonetaryAmountStrict, SUPPORTED_CURRENCIES } from '../modules/currency';
import { deserializeValue, serializeValue } from '../modules/filter-helper';

import AmountInput from './AmountInput';

const useStyles = makeStyles<Theme, { marginTop: boolean }>((theme) => ({
  label: {
    display: 'block',
    marginBottom: theme.spacing(-0.5),
    marginTop: ({ marginTop }) => (marginTop ? 8 : 0),
  },
  textField: {
    marginTop: theme.spacing(-0.5),
  },
}));

const formatMarkLabel = (num: number) => {
  if (num > 999 && num < 1000000) {
    return `${(num / 1000).toFixed(0)}K`; // convert to K for number from > 1000 < 1 million
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(0)}M`; // convert to M for number from > 1 million
  }
  if (num < 900) {
    return num; // if value < 1000, nothing to do
  }
  return '';
};

const CurrencySliderInput = () => {
  const { t } = useTranslation();
  const name = 'purchasePriceGross';
  const min = 0;
  const max = 1_000_000;
  const classes = useStyles({ marginTop: false });
  const { currency, convert } = useCurrency();
  const marks = [
    {
      value: 0,
      scaledValue: 0,
      label: '0',
    },
    {
      value: 90,
      scaledValue: convert(200_000, { from: DEFAULT_CURRENCY.code }),
      label: formatMarkLabel(convert(200_000, { from: DEFAULT_CURRENCY.code })),
    },
    {
      value: 100,
      scaledValue: convert(1_000_000, { from: DEFAULT_CURRENCY.code }),
      label: formatMarkLabel(convert(1_000_000, { from: DEFAULT_CURRENCY.code })),
    },
  ];
  const scale = scaleLinear()
    .domain(marks.map((mark) => mark.scaledValue))
    .range(marks.map((mark) => mark.value));
  const step = scale(100);
  const minLocalized = convert(min, { from: DEFAULT_CURRENCY.code });
  const maxLocalized = convert(max, { from: DEFAULT_CURRENCY.code });
  const { setValue: setFormValue, getValues: getFormValue, control } = useFormContext();
  const [value, setValue] = useState<[number | '', number | '']>(
    deserializeValue(getFormValue(name), (v, c) => scale(convert(v, { from: c }))),
  );
  const formValue = useWatch({ name, control }) as [MonetaryAmountStrict, MonetaryAmountStrict];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFormValueDebounced = useCallback(debounce(setFormValue, 400), [setFormValue]);

  // populate internal state updates
  useDidUpdateEffect(() => {
    setFormValueDebounced(
      name,
      serializeValue(
        [value[0] === '' ? '' : scale.invert(value[0]), value[1] === '' ? '' : scale.invert(value[1])],
        currency,
      ),
    );

    return () => {
      setFormValueDebounced.cancel();
    };
  }, [value, name, setFormValueDebounced]);

  // sync external state updates
  useDidUpdateEffect(() => {
    setValue(deserializeValue(formValue, (v, c) => scale(convert(v, { from: c }))));
  }, [setValue, formValue, convert]);

  return (
    <div>
      <InputLabel
        style={{
          marginBlockEnd: 2,
          fontWeight: 'normal',
          fontSize: 12,
          color: grey[600],
        }}
      >
        {t('car.prices.purchasePriceGross')}
      </InputLabel>
      <MuiSlider
        value={[value[0] === '' ? 0 : value[0], value[1] === '' ? 100 : value[1]]}
        step={step}
        aria-labelledby={name}
        onChange={(_, changeValue) => {
          setValue(changeValue as [number, number]);
        }}
        color="secondary"
        min={0}
        max={100}
        marks={marks}
      />
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <AmountInput<TextFieldProps>
            className={classes.textField}
            customInput={TextField}
            inputProps={{
              step,
              min: minLocalized,
              max: maxLocalized,
              'aria-labelledby': name,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{SUPPORTED_CURRENCIES[currency].symbol}</InputAdornment>,
            }}
            margin="dense"
            value={value[0] === '' ? '' : Math.round(scale.invert(value[0]))}
            variant="outlined"
            emptyInputAllowed
            onChange={(event) => {
              const raw = event.target.value?.replaceAll(/\D/g, '') ?? '';
              setValue((prev) => [raw === '' ? '' : scale(parseInt(raw, 10)), prev[1]]);
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <AmountInput<TextFieldProps>
            className={classes.textField}
            customInput={TextField}
            inputProps={{
              step,
              min: minLocalized,
              max: maxLocalized,
              'aria-labelledby': name,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{SUPPORTED_CURRENCIES[currency].symbol}</InputAdornment>,
            }}
            margin="dense"
            variant="outlined"
            value={value[1] === '' ? '' : Math.round(scale.invert(value[1]))}
            emptyInputAllowed
            onChange={(event) => {
              const raw = event.target.value?.replaceAll(/\D/g, '') ?? '';
              setValue((prev) => [prev[0], raw === '' ? '' : scale(parseInt(raw, 10))]);
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(CurrencySliderInput);
